// ============================================================
// FINANCE PAGE — Consolidated finance dashboard
// Route: /admin/finance
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  DollarSign, TrendingUp, Percent, Wallet, RefreshCw,
  CheckCircle, Clock, XCircle, AlertTriangle, Download,
} from 'lucide-react';
import { Badge, Button, StatCard, SearchInput, Select, Pagination } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { adminApi } from '../services/apiService';
import { formatDate, formatCurrency, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';

const Spinner = () => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    <div style={{ width: 36, height: 36, border: '3px solid rgba(232,93,4,0.3)', borderTopColor: '#E85D04', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
    <p>Loading data…</p>
  </div>
);

const Empty = ({ icon: Icon, title }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    {Icon && <Icon size={44} style={{ opacity: 0.2, marginBottom: 12 }} />}
    <p style={{ fontWeight: 700 }}>{title}</p>
  </div>
);

const TabButton = ({ active, onClick, label, icon: Icon }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 20px', cursor: 'pointer',
    background: 'none', border: 'none',
    borderBottom: active ? '2px solid #E85D04' : '2px solid transparent',
    color: active ? '#E85D04' : 'var(--admin-text-muted)',
    fontWeight: active ? 700 : 500, fontSize: 13,
    whiteSpace: 'nowrap',
  }}>
    {Icon && <Icon size={15} />} {label}
  </button>
);

// ── Revenue Dashboard Tab ─────────────────────────────────────
const RevenueDashboard = () => {
  const [data, setData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      adminApi.getRevenueAnalytics(period, () => null),
      adminApi.getOrderAnalytics(period, () => null),
    ]).then(([rev, ord]) => {
      setData(rev.value);
      setOrderData(ord.value);
      setLoading(false);
    });
  }, [period]);

  if (loading) return <Spinner />;

  const totalRevenue = data?.total_revenue ?? data?.totalRevenue ?? 0;
  const totalOrders  = orderData?.total_orders ?? orderData?.totalOrders ?? 0;
  const avgOrderVal  = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const platformEarnings = totalRevenue * 0.15; // approx

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {['daily', 'weekly', 'monthly', 'yearly'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{
            padding: '6px 16px', borderRadius: 8, cursor: 'pointer', textTransform: 'capitalize',
            border: '1px solid var(--admin-border)', fontSize: 12, fontWeight: 600,
            background: period === p ? '#E85D04' : 'var(--admin-surface)',
            color: period === p ? '#fff' : 'var(--admin-text-secondary)',
          }}>{p}</button>
        ))}
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Revenue" value={formatCurrency(totalRevenue)} icon={<DollarSign size={18} />} color="#10B981" />
        <StatCard label="Platform Earnings" value={formatCurrency(platformEarnings)} icon={<TrendingUp size={18} />} color="#E85D04" />
        <StatCard label="Avg Order Value" value={formatCurrency(avgOrderVal)} icon={<Percent size={18} />} color="#3B82F6" />
        <StatCard label="Total Orders" value={totalOrders.toLocaleString()} icon={<Wallet size={18} />} color="#8B5CF6" />
      </div>

      {/* Revenue by Restaurant */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Revenue Overview</h3>
          <span style={{ fontSize: 12, color: 'var(--admin-text-muted)', textTransform: 'capitalize' }}>Period: {period}</span>
        </div>
        <div className="admin-card-body">
          {data ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              {[
                { label: 'Gross Revenue', value: formatCurrency(totalRevenue), color: '#10B981' },
                { label: 'Net Revenue', value: formatCurrency(totalRevenue * 0.85), color: '#3B82F6' },
                { label: 'Commission Collected', value: formatCurrency(platformEarnings), color: '#E85D04' },
                { label: 'Refunds Issued', value: formatCurrency(data?.refunds ?? data?.total_refunds ?? 0), color: '#EF4444' },
                { label: 'Chargebacks', value: data?.chargebacks ?? 0, color: '#DC2626' },
                { label: 'Successful Payments', value: `${data?.success_rate ?? 98}%`, color: '#10B981' },
              ].map(item => (
                <div key={item.label} style={{ padding: 16, borderRadius: 10, border: '1px solid var(--admin-border)', background: 'var(--admin-surface)' }}>
                  <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginBottom: 6 }}>{item.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={DollarSign} title="No revenue data available" />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Restaurant Payouts Tab ────────────────────────────────────
const RestaurantPayouts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    adminApi.getSettlements(() => [])
      .then(res => { setData(Array.isArray(res) ? res : []); setLoading(false); })
      .catch(() => { setData([]); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(data, ['restaurantName', 'restaurant_name']);
  const final = statusFilter ? filtered.filter(s => s.status === statusFilter) : filtered;
  const { currentPage, totalPages, paginated, goTo } = usePagination(final);

  if (loading) return <Spinner />;

  const pending = data.filter(s => s.status === 'pending').reduce((s, x) => s + (x.netAmount || x.net_amount || 0), 0);
  const paid    = data.filter(s => s.status === 'paid').reduce((s, x) => s + (x.netAmount || x.net_amount || 0), 0);

  return (
    <div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Settlements" value={data.length} icon={<Wallet size={18} />} color="#3B82F6" />
        <StatCard label="Pending Payout" value={formatCurrency(pending)} icon={<Clock size={18} />} color="#F59E0B" />
        <StatCard label="Total Paid Out" value={formatCurrency(paid)} icon={<CheckCircle size={18} />} color="#10B981" />
        <StatCard label="This Week" value={formatCurrency(data.filter(s => {
          const d = new Date(s.createdAt || s.created_at || 0);
          return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
        }).reduce((s, x) => s + (x.netAmount || x.net_amount || 0), 0))} icon={<TrendingUp size={18} />} color="#E85D04" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchInput value={query} onChange={setQuery} placeholder="Search restaurant..." />
        <Select value={statusFilter} onChange={setStatusFilter} placeholder="All Status"
          options={[{ value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }, { value: 'failed', label: 'Failed' }]} />
        <Button variant="secondary" icon={<Download size={13} />} onClick={() => downloadCSV(data, 'settlements')}>Export</Button>
      </div>

      {data.length === 0 ? <Empty icon={Wallet} title="No settlements found" /> : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Restaurant', 'Period', 'Gross Revenue', 'Commission', 'Net Payout', 'Status', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((s, i) => (
                <tr key={s.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 700 }}>{s.restaurantName || s.restaurant_name || '—'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)' }}>{s.period || formatDate(s.period_start)}</td>
                  <td style={{ padding: '12px 16px', color: '#10B981', fontWeight: 700 }}>{formatCurrency(s.grossRevenue || s.gross_amount || 0)}</td>
                  <td style={{ padding: '12px 16px', color: '#E85D04' }}>{formatCurrency(s.commissionAmount || s.commission_amount || 0)}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 800 }}>{formatCurrency(s.netAmount || s.net_amount || 0)}</td>
                  <td style={{ padding: '12px 16px' }}><Badge status={s.status} /></td>
                  <td style={{ padding: '12px 16px' }}>
                    {s.status === 'pending' && (
                      <Button variant="success" size="sm"
                        onClick={async () => {
                          try { await adminApi.markSettlementPaid(s.id); setData(p => p.map(x => x.id === s.id ? { ...x, status: 'paid' } : x)); toast.success('Marked as paid'); }
                          catch { toast.error('Failed to update'); }
                        }}>Mark Paid</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={final.length} itemsPerPage={10} onPageChange={goTo} />
        </div>
      )}
    </div>
  );
};

// ── Refund Tracking Tab ───────────────────────────────────────
const RefundTracking = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getCancellations(() => [])
      .then(res => { setOrders(Array.isArray(res) ? res : []); setLoading(false); })
      .catch(() => { setOrders([]); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(orders, ['id', 'customerName', 'restaurantName']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  if (loading) return <Spinner />;

  const totalRefunded = orders.filter(o => o.refundStatus === 'refunded').reduce((s, o) => s + (o.total || 0), 0);
  const pendingRefunds = orders.filter(o => o.refundStatus === 'pending' || !o.refundStatus).length;

  return (
    <div>
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Cancelled" value={orders.length} icon={<XCircle size={18} />} color="#EF4444" />
        <StatCard label="Pending Refunds" value={pendingRefunds} icon={<Clock size={18} />} color="#F59E0B" />
        <StatCard label="Total Refunded" value={formatCurrency(totalRefunded)} icon={<DollarSign size={18} />} color="#10B981" />
        <StatCard label="Refund Rate" value={orders.length > 0 ? `${Math.round((orders.filter(o => o.refundStatus === 'refunded').length / orders.length) * 100)}%` : '0%'} icon={<Percent size={18} />} color="#3B82F6" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <SearchInput value={query} onChange={setQuery} placeholder="Search by order ID, customer..." />
        <Button variant="secondary" icon={<Download size={13} />} onClick={() => downloadCSV(orders, 'refunds')}>Export</Button>
      </div>

      {orders.length === 0 ? <Empty icon={DollarSign} title="No cancellations found" /> : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Order ID', 'Customer', 'Restaurant', 'Amount', 'Reason', 'Refund Status', 'Date'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((o, i) => (
                <tr key={o.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                  <td style={{ padding: '12px 16px' }}><code style={{ fontSize: 11 }}>{(o.id || '').slice(0, 12)}…</code></td>
                  <td style={{ padding: '12px 16px' }}>{o.customerName || o.customer_name || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>{o.restaurantName || o.restaurant_name || '—'}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 700 }}>{formatCurrency(o.total || 0)}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>{o.cancelReason || o.cancel_reason || '—'}</td>
                  <td style={{ padding: '12px 16px' }}><Badge status={o.refundStatus || 'pending'} /></td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>{formatDate(o.createdAt || o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
        </div>
      )}
    </div>
  );
};

// ── Commission Config Tab ─────────────────────────────────────
const CommissionConfig = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getCommissionSettings(() => [])
      .then(res => { setSettings(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(() => { setSettings([]); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(settings, ['restaurantName', 'restaurant_name', 'name']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ padding: 16, borderRadius: 10, background: 'rgba(232,93,4,0.08)', border: '1px solid rgba(232,93,4,0.2)', marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>⚙️ Platform Default Commission</div>
        <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>Default commission rate applied to all new restaurants: <strong style={{ color: '#E85D04' }}>15%</strong></div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <SearchInput value={query} onChange={setQuery} placeholder="Search restaurant..." />
      </div>

      {settings.length === 0 ? <Empty icon={Percent} title="No commission settings found" /> : (
        <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--admin-bg)' }}>
                {['Restaurant', 'Commission Rate', 'Type', 'Effective Date', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((s, i) => (
                <tr key={s.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 700 }}>{s.restaurantName || s.restaurant_name || s.name || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: '#E85D04' }}>{s.rate ?? s.commission_rate ?? 15}%</span>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)' }}>{s.type || 'Percentage'}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--admin-text-muted)', fontSize: 12 }}>{formatDate(s.effectiveDate || s.effective_date)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <Button variant="secondary" size="sm" onClick={() => toast.info('Edit commission coming soon')}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
        </div>
      )}
    </div>
  );
};

// ── Main Finance Page ─────────────────────────────────────────
const TABS = [
  { id: 'revenue',     label: 'Revenue Dashboard',    icon: TrendingUp },
  { id: 'payouts',     label: 'Restaurant Payouts',   icon: Wallet },
  { id: 'refunds',     label: 'Refund Tracking',      icon: RefreshCw },
  { id: 'commission',  label: 'Commission Config',     icon: Percent },
];

const Finance = () => {
  const [activeTab, setActiveTab] = useState('revenue');

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Finance</h1>
          <p className="admin-page-subtitle">Revenue, payouts, refunds and commission management</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-border)', marginBottom: 24, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {TABS.map(tab => <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} label={tab.label} icon={tab.icon} />)}
      </div>

      {activeTab === 'revenue'    && <RevenueDashboard />}
      {activeTab === 'payouts'    && <RestaurantPayouts />}
      {activeTab === 'refunds'    && <RefundTracking />}
      {activeTab === 'commission' && <CommissionConfig />}
    </div>
  );
};

export default Finance;
